import { parseVehicleExceptions } from '@/utils/addonVehicleApplicability';
import type {
  AddonSettings,
  AddonSuggestion,
  AddonSuggestionType,
  EvaluatedJob,
} from '@/types/addonSuggestions';

/**
 * CORE-BP item 6 — the settled work state a post-add evaluation is asked about.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS A MODULE AND NOT INLINE IN EACH ADD PATH
 * ═══════════════════════════════════════════════════════════════════════════
 * Six production paths can put work on an existing ticket. If each of them
 * decided for itself what "the ticket's current work" means, what counts as a
 * suggestion already satisfied, and which settings apply, they would disagree
 * within a release — that is exactly how Slice B found two handlers writing
 * different outcomes for the same event.
 *
 * So the decisions live here, once, as pure functions over already-loaded data.
 * The provider does the IO; this decides what the answer is.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE RE-ENTRANCY PROBLEM ITEM 6 CREATES
 * ═══════════════════════════════════════════════════════════════════════════
 * At ticket creation, evaluation ran ONCE, so "is this already on the ticket?"
 * never arose. Evaluating after every add makes it the central question:
 * accepting an alignment suggestion adds an alignment job, which is itself work
 * added to the ticket, which triggers another evaluation. Without a guard that
 * loop is infinite and the advisor is asked the same question forever.
 *
 * The guard is deliberately narrow. It suppresses a family only when the
 * SHOP'S OWN CONFIGURED TARGET for that family is demonstrably already on the
 * ticket — by catalogue identity, or failing that by an exact match on the
 * configured job's label. It does not suppress a family because something with
 * a similar-looking name is present, and it does not suppress evaluation
 * wholesale for work that arrived from a suggestion: an accepted suggestion
 * becomes ordinary work, and if it truthfully creates a new obligation under
 * the shop's rules the engine may say so.
 */

/** One job currently on the ticket, with whatever identity it actually has. */
export interface TicketWorkItem {
  /** `jobs.catalog_job_id` — the canonical identity, when the job has one. */
  catalogJobId?: string | null;
  /** `jobs.name`. Always present; the only evidence a custom job carries. */
  name: string;
}

/** The four families' configured target labels, when they can be resolved. */
export type AddonCatalogLabels = Partial<Record<AddonSuggestionType, string>>;

const normalize = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * The ticket's complete current work, in the shape the evaluator takes.
 *
 * Catalogue identity is carried through rather than flattened to a name — the
 * defect Slice A closed, and re-introducing it here would silently downgrade
 * every post-add evaluation to keyword matching.
 */
export function toEvaluatedJobs(work: readonly TicketWorkItem[]): EvaluatedJob[] {
  return work.map((w) => ({ catalogJobId: w.catalogJobId ?? null, name: w.name }));
}

/**
 * Is the shop's configured target for this family already on the ticket?
 *
 * Answered by identity first. The fallback is exact normalised label equality,
 * not similarity: "Four-wheel alignment" matches "four-wheel  alignment" and
 * nothing else. A shop that has configured no target for the family cannot
 * have this question answered at all — and the honest response to an
 * unanswerable question is to ask the human, because declining is free and
 * silently withholding a real technical obligation is not.
 */
export function isFamilyAlreadyOnTicket(
  type: AddonSuggestionType,
  work: readonly TicketWorkItem[],
  settings: Pick<AddonSettings, 'catalogJobIds'>,
  catalogLabels: AddonCatalogLabels = {},
): boolean {
  const configuredId = settings.catalogJobIds?.[type];
  if (configuredId) {
    if (work.some((w) => w.catalogJobId && w.catalogJobId === configuredId)) return true;
  }

  const configuredLabel = catalogLabels[type];
  if (configuredLabel) {
    const target = normalize(configuredLabel);
    if (work.some((w) => normalize(w.name) === target)) return true;
  }

  return false;
}

/**
 * Drop suggestions whose target is already present.
 *
 * This is the whole re-entrancy guard, and it is worth stating what it is NOT:
 * it does not remember what was shown before, does not suppress a family
 * because it was declined, and adds no nag policy. The shop's never-prompt
 * terms remain the only suppression authority, exactly as before — the loop is
 * broken by the work existing, not by anyone being told to stop asking.
 */
export function filterSuggestionsAlreadyOnTicket(
  suggestions: readonly AddonSuggestion[],
  work: readonly TicketWorkItem[],
  settings: Pick<AddonSettings, 'catalogJobIds'>,
  catalogLabels: AddonCatalogLabels = {},
): AddonSuggestion[] {
  return suggestions.filter(
    (s) => !isFamilyAlreadyOnTicket(s.type, work, settings, catalogLabels));
}

/* ══════════════════════════ shop settings, once ═══════════════════════════ */

/** The row shape as stored. Kept local so callers need no generated type. */
export interface AddonSettingsRow {
  always_prompt_terms?: string[] | null;
  never_prompt_terms?: string[] | null;
  adas_year_threshold?: number | null;
  alignment_default_checked?: boolean | null;
  sas_default_checked?: boolean | null;
  adas_default_checked?: boolean | null;
  tpms_default_checked?: boolean | null;
  feature_enabled?: boolean | null;
  precision_first_mode?: boolean | null;
  alignment_catalog_job_id?: string | null;
  sas_catalog_job_id?: string | null;
  adas_catalog_job_id?: string | null;
  tpms_catalog_job_id?: string | null;
  /** CORE-BP item 2 — shop-authored vehicle applicability exceptions. */
  vehicle_exceptions?: unknown;
}

/**
 * The shop's current BP settings, with the same defaults ticket creation uses.
 *
 * A shop that has never opened the settings page has no row, and the absence
 * means "the defaults", not "the feature is off" — which is why a missing row
 * still yields `featureEnabled: true`. Post-add evaluation and creation-time
 * evaluation must agree about that, or the same vehicle would be treated
 * differently depending on when the work was added.
 */
export function mapAddonSettingsRow(row: AddonSettingsRow | null | undefined): AddonSettings {
  return {
    alwaysPromptTerms: row?.always_prompt_terms || [],
    neverPromptTerms: row?.never_prompt_terms || [],
    adasYearThreshold: row?.adas_year_threshold || 2016,
    alignmentDefaultChecked: row?.alignment_default_checked ?? true,
    sasDefaultChecked: row?.sas_default_checked ?? true,
    adasDefaultChecked: row?.adas_default_checked ?? false,
    tpmsDefaultChecked: row?.tpms_default_checked ?? false,
    featureEnabled: row?.feature_enabled ?? true,
    precisionFirstMode: row?.precision_first_mode ?? true,
    catalogJobIds: {
      alignment: row?.alignment_catalog_job_id ?? undefined,
      sas: row?.sas_catalog_job_id ?? undefined,
      adas: row?.adas_catalog_job_id ?? undefined,
      tpms: row?.tpms_catalog_job_id ?? undefined,
    },
    // CORE-BP item 2. Absent or malformed reads as NO policy, which is the
    // behaviour every shop had before the column existed.
    vehicleExceptions: parseVehicleExceptions(row?.vehicle_exceptions),
  };
}
